import { colors } from '../../../theme/tokens/colors';
import { chat as defaultAccent } from '../../../theme/tokens/semantic';
import { surfaces, text, interactive, states, borders } from '../../../theme/tokens/semantic';
import { radius, radiusPx } from '../../../theme/tokens/radius';
import { shadows as defaultShadows } from '../../../theme/tokens/shadows';
import { typography, typographyStyles } from '../../../theme/tokens/typography';

const PAGE_MIN_HEIGHT = 'calc(100vh - 82px)';

/**
 * @param {{ accent?: object, shadows?: object }} [tokenOverrides]
 */
export function createBusinessFunctionsSx(tokenOverrides = {}) {
  const accent = tokenOverrides.accent ?? defaultAccent;
  const shadowTokens = tokenOverrides.shadows ?? defaultShadows;

  return {
    // ── Existing page-level tokens (unchanged) ──────────────────────
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: PAGE_MIN_HEIGHT,
      bgcolor: surfaces.background,
      px: { xs: 2, md: 3 },
      py: 2.5,
      gap: 2.5,
      // gap:0,
    },
    pageHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      minWidth: 0,
    },
    hubActionRow: {
      display: 'grid',
      width: '100%',
      alignItems: 'flex-end',
      gap: 2,
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        md: 'minmax(280px, 360px) minmax(0, 1fr)',
      },
    },
    hubOrgFilter: {
      minWidth: { xs: '100%', sm: 280 },
      maxWidth: { sm: 360 },
      width: { xs: '100%', sm: 320 },
      flexShrink: 0,
    },
    pageTitle: {
      fontWeight: 700,
      color: accent.textPrimary ?? text.primary,
    },
    pageSubtitle: {
      color: accent.textSecondary ?? text.secondary,
    },
    hubCardInactive: {
      opacity: 0.72,
    },
    hubCardHeaderRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1,
    },
    hubCardToggle: {
      flexShrink: 0,
      ml: 0.5,
    },
    metaRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      mt: 0.5,
    },
    metaChip: {
      px: 1,
      py: 0.375,
      borderRadius: 999,
      bgcolor: accent.primaryMuted ?? colors.grey[100],
      color: accent.textSecondary ?? text.secondary,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightMedium,
    },
    panelGrid: {
      mt: 0.5,
    },
    panel: {
      height: '100%',
      borderRadius: 0,
      border: `1px solid ${accent.divider ?? borders.border}`,
      bgcolor: surfaces.surface,
      boxShadow: 'none',
      p: 2.25,
      minHeight: 160,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    panelTitle: {
      fontWeight: typography.fontWeightSemibold,
      fontSize: '0.95rem',
      color: accent.textPrimary ?? text.primary,
    },
    panelBody: {
      fontSize: typography.fontSizeSm,
      color: accent.textSecondary ?? text.secondary,
      lineHeight: typography.lineHeightBody,
      flex: 1,
    },
    placeholderBar: {
      height: 10,
      borderRadius: radiusPx.sm,
      bgcolor: colors.grey[100],
      width: '70%',
    },
    placeholderBarShort: {
      height: 10,
      borderRadius: radiusPx.sm,
      bgcolor: colors.grey[100],
      width: '45%',
      mt: 1,
    },
    emptyHint: {
      mt: 1,
      p: 2,
      borderRadius: 0,
      border: `1px dashed ${accent.divider ?? borders.border}`,
      bgcolor: surfaces.surface,
      color: accent.textSecondary ?? text.secondary,
      boxShadow: shadowTokens.xs,
    },

    // ── Account Detail Page ─────────────────────────────────────────

    // Account Header — name row + status badge + action buttons
    accountHeaderRoot: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: 'wrap',
    },
    accountHeaderLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      flex: 1,
      minWidth: 0,
    },
    accountTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
    },
    accountTitle: {
      ...typographyStyles.pageTitle,
      color: text.primary,
      lineHeight: 1.2,
    },
    accountDescription: {
      fontSize: typography.fontSizeMd,
      color: text.secondary,
      lineHeight: typography.lineHeightBody,
      mt: 0.25,
    },
    statusBadgeActive: {
      px: 1.25,
      py: 0.25,
      borderRadius: radiusPx.pill,
      bgcolor: states.successSoft,
      color: states.success,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightSemibold,
      lineHeight: 1.5,
      textTransform: 'capitalize',
    },
    statusBadgeInactive: {
      px: 1.25,
      py: 0.25,
      borderRadius: radiusPx.pill,
      bgcolor: states.dangerSoft,
      color: states.danger,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightSemibold,
      lineHeight: 1.5,
      textTransform: 'capitalize',
    },
    accountHeaderActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexShrink: 0,
    },
    accountDetailsBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightMedium,
      fontSize: typography.fontSizeSm,
      borderColor: borders.border,
      color: text.primary,
      borderRadius: 0,
      px: 2,
      '&:hover': {
        borderColor: borders.borderStrong,
        bgcolor: surfaces.surfaceSubtle,
      },
    },

    // ── Tab Bar ──────────────────────────────────────────────────────
    tabBarRoot: {
      borderBottom: `1px solid ${borders.border}`,
      // mt: 1,
      minHeight: 48,
    },
    workspaceTableCard: {
      width: '100%',
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: radiusPx.md,
    },
    tabBarSkeletonRow: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 2.5,
      minHeight: 48,
      px: 2,
      pb: 1,
    },
    tabBarInlineRow: {
      display: 'flex',
      alignItems: 'flex-end',
      width: '100%',
    },
    tabBarTabs: {
      flex: 1,
      minWidth: 0,
    },
    tabBarTabsInline: {
      flex: '0 1 auto',
      maxWidth: '100%',
    },
    tabBarAddTab: {
      minWidth: 48,
      minHeight: 48,
      px: 1.5,
      opacity: 1,
      color: accent.primary ?? interactive.primary,
      '&.Mui-selected': {
        color: accent.primary ?? interactive.primary,
      },
      '& .MuiTab-iconWrapper': {
        margin: 0,
      },
    },
    tabBarIndicator: {
      backgroundColor: accent.primary ?? interactive.primary,
      height: 2.5,
      borderRadius: '2px 2px 0 0',
    },
    tabItem: {
      textTransform: 'none',
      fontWeight: typography.fontWeightMedium,
      fontSize: typography.fontSizeMd,
      color: text.muted,
      minWidth: 'auto',
      px: 2,
      py: 1.5,
      '&.Mui-selected': {
        color: accent.primary ?? interactive.primary,
        fontWeight: typography.fontWeightSemibold,
      },
    },
    tabPanelContainer: {
      // minHeight: 480,
      minHeight:'auto',
      width: '100%',
      position: 'relative',
    },

    // ── Overview Tab — Stat Cards ───────────────────────────────────
    statCardGridRoot: {
      mt: 2,
    },
    statCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      boxShadow: shadowTokens.xs,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '&:hover': {
        boxShadow: shadowTokens.md,
        borderColor: borders.borderStrong,
      },
    },
    statCardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    statCardTitle: {
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightMedium,
      color: text.secondary,
      lineHeight: typography.lineHeightBody,
    },
    statCardIconWrap: {
      width: 36,
      height: 36,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    statCardValue: {
      fontSize: typography.fontSize3xl,
      fontWeight: typography.fontWeightBold,
      color: text.primary,
      lineHeight: typography.lineHeightTight,
    },
    statCardTrendUp: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightMedium,
      color: states.success,
    },
    statCardTrendDown: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightMedium,
      color: states.danger,
    },
    statCardTrendFlat: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightMedium,
      color: text.muted,
    },
    statCardFooter: {
      fontSize: typography.fontSizeXs,
      color: text.muted,
      mt: 0.25,
    },

    // ── Overview Tab — Analytics Chart ──────────────────────────────
    chartPanelRoot: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      boxShadow: shadowTokens.xs,
      p: 2.5,
      mt: 2.5,
    },
    chartPanelHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 2,
      flexWrap: 'wrap',
      gap: 1,
    },
    chartPanelTitle: {
      ...typographyStyles.sectionTitle,
      color: text.primary,
    },
    chartPanelControls: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    chartControlSelect: {
      '& .MuiSelect-select': {
        fontSize: typography.fontSizeXs,
        py: 0.5,
        px: 1.25,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: borders.border,
        borderRadius: 0,
      },
      minWidth: 100,
    },
    chartEmptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 260,
      color: text.muted,
      fontSize: typography.fontSizeSm,
    },

    // ── Overview Tab — Activity Timeline ────────────────────────────
    activityTimelineRoot: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      boxShadow: shadowTokens.xs,
      p: 2.5,
      mt: 2.5,
    },
    activityTimelineTitle: {
      ...typographyStyles.sectionTitle,
      color: text.primary,
      mb: 2,
    },
    activityItem: {
      display: 'flex',
      gap: 1.5,
      py: 1.25,
      borderBottom: `1px solid ${colors.grey[100]}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    activityDot: {
      width: 8,
      height: 8,
      borderRadius: radius.full,
      bgcolor: accent.primary ?? interactive.primary,
      mt: 0.75,
      flexShrink: 0,
    },
    activityContent: {
      flex: 1,
      minWidth: 0,
    },
    activityText: {
      fontSize: typography.fontSizeSm,
      color: text.primary,
      lineHeight: typography.lineHeightBody,
    },
    activityTime: {
      fontSize: typography.fontSizeXs,
      color: text.muted,
      mt: 0.25,
    },
    activityEmpty: {
      py: 3,
      textAlign: 'center',
      color: text.muted,
      fontSize: typography.fontSizeSm,
    },

    // ── Workspaces Tab ──────────────────────────────────────────────
    workspacesHeaderRoot: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mt: 2,
      mb: 2,
      flexWrap: 'wrap',
      gap: 1.5,
    },
    workspacesHeaderLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
    },
    workspacesTitle: {
      ...typographyStyles.sectionTitle,
      color: text.primary,
    },
    workspacesSubtitle: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
    },
    workspacesNewBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightSemibold,
      fontSize: typography.fontSizeSm,
      borderRadius: 2,
      px: 2.5,
      py: 0.71,
      bgcolor: accent.primary ?? states.success,
      color: accent.primaryContrast ?? colors.white,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: accent.primaryDark ?? states.success,
        boxShadow: shadowTokens.sm,
      },
    },
    workspaceCardGridRoot: {
      mt: 0.5,
    },

    // ── Workspace Card ──────────────────────────────────────────────
    workspaceCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      boxShadow: 'none',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      cursor: 'pointer',
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '&:hover': {
        boxShadow: shadowTokens.md,
        borderColor: borders.borderStrong,
      },
    },
    workspaceCardInactive: {
      opacity: 0.72,
    },
    workspaceCardToggle: {
      flexShrink: 0,
      ml: 0.5,
    },
    workspaceCardTop: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1.5,
    },
    workspaceCardInfo: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      flex: 1,
      minWidth: 0,
    },
    workspaceIconWrap: {
      width: 48,
      height: 48,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    workspaceCardTextBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
      minWidth: 0,
    },
    workspaceCardName: {
      ...typographyStyles.cardTitle,
      color: text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    workspaceCardDescription: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      lineHeight: typography.lineHeightBody,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    workspaceCardMenuBtn: {
      color: text.muted,
      '&:hover': {
        color: text.primary,
        bgcolor: surfaces.surfaceSubtle,
      },
    },
    workspaceCardMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexWrap: 'wrap',
      pt: 0.5,
      borderTop: `1px solid ${colors.grey[100]}`,
    },
    workspaceMetaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: typography.fontSizeXs,
      color: text.muted,
    },
    workspaceMetaIcon: {
      fontSize: '14px',
      color: text.muted,
    },

    // ── Empty States ────────────────────────────────────────────────
    workspaceEmptyRoot: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      gap: 2,
      bgcolor: surfaces.surface,
      borderRadius: 0,
      border: `1px dashed ${borders.border}`,
    },
    workspaceEmptyIcon: {
      fontSize: 56,
      color: colors.grey[300],
    },
    workspaceEmptyTitle: {
      ...typographyStyles.sectionTitle,
      color: text.primary,
    },
    workspaceEmptySubtitle: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      textAlign: 'center',
      maxWidth: 360,
    },

    // ── Skeleton Loaders ────────────────────────────────────────────
    skeletonCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${colors.grey[100]}`,
      borderRadius: 0,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    },

    // ── Icon accent background helpers (resolved at render time) ───
    iconAccent: {
      success: { bgcolor: states.successSoft, color: states.success },
      info: { bgcolor: states.infoSoft, color: states.info },
      warning: { bgcolor: states.warningSoft, color: states.warning },
      danger: { bgcolor: states.dangerSoft, color: states.danger },
    },

    // ── Account Details Tab Form ────────────────────────────────────
    detailsTabRoot: {
      mt: 2,
    },
    detailsFormCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      p: { xs: 2.5, md: 3.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
    },
    detailsFormSectionTitle: {
      ...typographyStyles.sectionTitle,
      color: text.primary,
      mb: 0.5,
    },
    detailsFormSectionSubtitle: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      mb: 2.5,
    },
    detailsFormActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 1.5,
      pt: 2,
      borderTop: `1px solid ${colors.grey[200]}`,
    },
    detailsSaveBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightSemibold,
      fontSize: typography.fontSizeSm,
      borderRadius: 0,
      px: 3,
      py: 1,
      bgcolor: accent.primary ?? states.success,
      color: accent.primaryContrast ?? colors.white,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: accent.primaryDark ?? states.success,
        boxShadow: shadowTokens.sm,
      },
    },
    detailsCancelBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightMedium,
      fontSize: typography.fontSizeSm,
      borderRadius: 0,
      px: 2.5,
      py: 1,
      color: text.secondary,
      borderColor: borders.border,
      '&:hover': {
        borderColor: borders.borderStrong,
        bgcolor: surfaces.surfaceSubtle,
      },
    },
    detailsReadOnlyBox: {
      p: 1.5,
      bgcolor: surfaces.surfaceSubtle,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      fontSize: typography.fontSizeSm,
      color: text.primary,
      fontFamily: 'monospace',
    },
    detailsReadOnlyField: {
      '& .MuiOutlinedInput-root': {
        bgcolor: surfaces.surfaceSubtle,
        cursor: 'default',
        '& fieldset': {
          borderColor: borders.border,
        },
        '&:hover fieldset': {
          borderColor: borders.border,
        },
        '&.Mui-focused fieldset': {
          borderColor: borders.border,
          borderWidth: 1,
        },
      },
      '& .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled': {
        cursor: 'default',
        color: text.primary,
        WebkitTextFillColor: text.primary,
        opacity: 1,
      },
      '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled': {
        top: '-5px',
        color: text.secondary,
        opacity: 1,
      },
      '& .MuiInputLabel-shrink': {
        top: '-1px',
      },
      '& .MuiFormHelperText-root': {
        color: text.secondary,
      },
    },

    // ── Workspace Detail View Styles ───────────────────────────────
    workspaceDetailHeaderRoot: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: { xs: 'wrap', md: 'nowrap' },
    },
    workspaceDetailIconWrap: {
      width: 48,
      height: 48,
      borderRadius: radiusPx.sm,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: states.successSoft,
      color: accent.primary ?? states.success,
      flexShrink: 0,
    },
    workspaceDetailTitle: {
      ...typographyStyles.pageTitle,
      color: text.primary,
    },
    workspaceDetailDescription: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      mt: 0.5,
    },
    workspaceDetailHeaderActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexShrink: 0,
    },
    workspaceShareBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightMedium,
      fontSize: typography.fontSizeSm,
      borderRadius: 0,
      borderColor: borders.border,
      color: text.primary,
      px: 2,
      '&:hover': {
        borderColor: borders.borderStrong,
        bgcolor: surfaces.surfaceSubtle,
      },
    },

    // ── Zero State Onboarding Card ────────────────────────────────
    zeroStateCard: {
      bgcolor: surfaces.surface,
      border: `1px dashed ${accent.primary ?? states.success}`,
      borderRadius: 0,
      p: { xs: 3, md: 5 },
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      mt: 3,
    },
    zeroStateTitle: {
      ...typographyStyles.sectionTitle,
      fontSize: '20px',
      color: text.primary,
    },
    zeroStateSubtitle: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      maxWidth: 540,
    },
    zeroStateCtaBtn: {
      textTransform: 'none',
      fontWeight: typography.fontWeightSemibold,
      fontSize: typography.fontSizeMd,
      borderRadius: 0,
      px: 3.5,
      py: 1.25,
      bgcolor: accent.primary ?? states.success,
      color: accent.primaryContrast ?? colors.white,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: accent.primaryDark ?? states.success,
        boxShadow: shadowTokens.sm,
      },
    },

    // ── Table Card Grid inside Workspace Overview ──────────────────
    tablesSectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mt: 3.5,
      mb: 1.5,
    },
    tableCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      cursor: 'pointer',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      '&:hover': {
        borderColor: accent.primary ?? interactive.primary,
        boxShadow: shadowTokens.xs,
      },
    },
    tableCardSelected: {
      borderColor: accent.primary ?? interactive.primary,
      boxShadow: shadowTokens.xs,
      bgcolor: accent.primaryMuted ?? surfaces.surfaceSubtle,
    },
    tableRecordsHeaderRoot: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.75,
      pb: 2.5,
      borderBottom: `1px solid ${borders.border}`,
    },
    tableRecordsBackLink: {
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 0.25,
      px: 0.75,
      py: 0.5,
      ml: -0.75,
      border: 'none',
      borderRadius: 0,
      bgcolor: 'transparent',
      color: accent.primary ?? interactive.primary,
      cursor: 'pointer',
      transition: 'color 140ms ease, background-color 140ms ease',
      '& .table-records-back-chevron': {
        transition: 'transform 140ms ease',
      },
      '&:hover': {
        color: accent.primaryDark ?? interactive.primary,
        bgcolor: accent.primaryMuted ?? surfaces.surfaceSubtle,
      },
      '&:hover .table-records-back-chevron': {
        transform: 'translateX(-2px)',
      },
      '&:focus-visible': {
        outline: `2px solid ${accent.primary ?? interactive.primary}`,
        outlineOffset: 2,
      },
    },
    tableRecordsBackIcon: {
      fontSize: 20,
      flexShrink: 0,
    },
    tableRecordsBackLabel: {
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightSemibold,
      lineHeight: 1.2,
      letterSpacing: '0.01em',
    },
    tableRecordsHero: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      minWidth: 0,
    },
    tableRecordsHeroText: {
      minWidth: 0,
      flex: 1,
    },
    tableRecordsIconWrap: {
      width: 42,
      height: 42,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: accent.primaryMuted ?? surfaces.surfaceSubtle,
      color: accent.primary ?? interactive.primary,
      border: `1px solid ${accent.primaryBorder ?? borders.border}`,
      flexShrink: 0,
    },
    tableRecordsTitle: {
      fontWeight: typography.fontWeightSemibold,
      fontSize: typography.fontSizeXl ?? '20px',
      color: text.primary,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    tableRecordsMeta: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1,
      mt: 0.5,
    },
    tableRecordsMetaItem: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      lineHeight: 1.4,
    },
    tableRecordsMetaDot: {
      width: 4,
      height: 4,
      borderRadius: '50%',
      bgcolor: text.muted ?? borders.border,
      flexShrink: 0,
    },
    tableCardIconWrap: {
      width: 38,
      height: 38,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: surfaces.surfaceSubtle,
      color: accent.primary ?? interactive.primary,
      flexShrink: 0,
    },
    tableCardAdd: {
      bgcolor: surfaces.surfaceSubtle,
      border: `1px dashed ${borders.border}`,
      borderRadius: 0,
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      cursor: 'pointer',
      height: '100%',
      minHeight: 80,
      '&:hover': {
        borderColor: accent.primary ?? states.success,
        bgcolor: surfaces.surface,
      },
    },

    // ── Recent Records Scroller / Chips ────────────────────────────
    recentRecordCard: {
      bgcolor: surfaces.surface,
      border: `1px solid ${borders.border}`,
      borderRadius: 0,
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minWidth: 180,
    },

    // ── Campaign Records (Phase 1) ─────────────────────────────────
    recordsToolbarRoot: {
      width: '100%',
      px:1.25
    },
    recordsTableToolbar: {
      width: '100%',
    },
    recordsToolbarActions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1,
      width: { xs: '100%', sm: 'auto' },
      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
    },
    recordsFilterSummary: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    recordsBulkBarRoot: {
      position: { xs: 'sticky', md: 'static' },
      bottom: { xs: 0, md: 'auto' },
      zIndex: 2,
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      justifyContent: 'space-between',
      gap: 1.5,
      px: { xs: 2, md: 0 },
      py: 1.5,
      bgcolor: surfaces.surface,
      borderTop: { xs: `1px solid ${borders.border}`, md: 'none' },
      borderBottom: `1px solid ${borders.border}`,
      boxShadow: { xs: shadowTokens.sm, md: 'none' },
    },
    recordsBulkBarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
    },
    recordsBulkCount: {
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightSemibold,
      color: text.primary,
    },
    recordsBulkBarActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      width: { xs: '100%', sm: 'auto' },
    },
    leadSendTemplateModalContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      minHeight: 0,
      flex: 1,
      px: { xs: 0, sm: 0 },
      py: { xs: 0.5, sm: 0.75 },
    },
    leadSendTemplateSetupSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      flexShrink: 0,
      p: { xs: 1.25, sm: 1.5 },
      borderRadius: radiusPx.sm,
      border: `1px solid ${borders.border}`,
      bgcolor: surfaces.surfaceSubtle,
    },
    leadSendTemplateSplitBody: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      mt: 0.5,
    },
    leadSendTemplateCoveragePanel: {
      p: 1.5,
      borderRadius: radiusPx.sm,
      border: `1px solid ${borders.border}`,
      bgcolor: surfaces.surfaceSubtle,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    leadSendTemplateTabPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      minHeight: 0,
      maxHeight: { xs: 'none', sm: 'min(52vh, 420px)' },
      overflowY: 'auto',
      pr: 0.5,
    },
    leadSendTemplateChipRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      overflowX: { xs: 'auto', sm: 'visible' },
      pb: { xs: 0.5, sm: 0 },
    },
    leadSendTemplateUploadZone: {
      border: `1px dashed ${borders.border}`,
      borderRadius: radiusPx.sm,
      p: 2,
      minHeight: 120,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      bgcolor: surfaces.surfaceSubtle,
      cursor: 'pointer',
      textAlign: 'center',
      '&:hover': {
        borderColor: interactive.hoverBorder ?? accent.primaryBorder,
        bgcolor: surfaces.surface,
      },
    },
    leadSendTemplatePreviewGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.5,
    },
    leadSendTemplateParamRow: {
      p: { xs: 1.125, sm: 1.25 },
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      borderRadius: radiusPx.sm,
      border: `1px solid ${borders.border}`,
      bgcolor: surfaces.surface,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
    },
    leadSendTemplateParamFieldCell: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.375,
      flex: '1 1 0',
      minWidth: 0,
    },
    leadSendTemplateParamFieldsGrid: {
      display: 'flex',
      flexDirection: 'row',
      gap: 1,
      width: '100%',
      alignItems: 'flex-start',
    },
    leadSendTemplateBulkMapGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      rowGap: 0.625,
      width: '100%',
      alignItems: 'stretch',
    },
    leadSendTemplateBulkMain: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      minWidth: 0,
      minHeight: 0,
    },
    leadSendTemplateBulkSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      p: 1.25,
      borderRadius: radiusPx.sm,
      border: `1px solid ${borders.border}`,
      bgcolor: surfaces.surface,
    },
    leadSendTemplateBulkSheetSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
    },
    leadSendTemplateBulkSheetPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      minHeight: 0,
    },
    leadSendTemplateBulkSplitGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' },
      gap: { xs: 1.5, md: 2 },
      alignItems: 'start',
      width: '100%',
    },
    leadSendTemplateBulkFieldCell: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.375,
      width: '100%',
      minWidth: 0,
    },
    leadSendTemplateParamFieldsGridStacked: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      columnGap: 1,
      rowGap: 0.5,
      width: '100%',
      alignItems: 'stretch',
    },
    leadSendTemplateParamFieldsGridSingle: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      rowGap: 0.5,
      width: '100%',
    },
    leadSendTemplateParamFieldLabel: {
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightMedium,
      color: text.secondary,
      lineHeight: 1.3,
      mb: 0,
    },
    leadSendTemplateSplitGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' },
      gap: { xs: 1.5, md: 2 },
      alignItems: 'start',
      width: '100%',
    },
    leadSendTemplateSplitMain: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 0,
      maxHeight: { md: 'min(62vh, 540px)' },
      overflowY: { md: 'auto' },
      pr: { md: 1 },
    },
    leadSendTemplateSplitMainFlush: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.875,
      minWidth: 0,
      minHeight: 0,
    },
    leadSendTemplateSplitPreview: {
      minWidth: 0,
      alignSelf: 'start',
      width: '100%',
      position: { md: 'sticky' },
      top: { md: 8 },
    },
    leadSendTemplateSummaryBar: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      fontSize: typography.fontSizeSm,
      color: text.secondary,
    },
    leadSendTemplateErrorPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      maxHeight: 180,
      overflowY: 'auto',
    },
    leadSendTemplateErrorDownloadRow: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 1,
      width: '100%',
    },
    leadSendTemplateFooterActions: {
      display: 'flex',
      flexDirection: { xs: 'column-reverse', sm: 'row' },
      justifyContent: { sm: 'flex-end' },
      alignItems: { sm: 'center' },
      gap: 1,
      width: '100%',
      '& > *': {
        width: { xs: '100%', sm: 'auto' },
      },
    },
    recordsFiltersDrawerMobile: {
      width: '100%',
      maxHeight: '85vh',
      borderRadius: 0,
    },
    recordsFiltersPopoverPaper: {
      width: 440,
      // height: 'min(80vh, 680px)',
      maxHeight: '60vh',
      borderRadius: 0,
      display: 'flex',
      overflow: 'hidden',
    },
    recordsFiltersDrawerDesktop: {
      width: { sm: 420, md: 480 },
      borderRadius: 0,
    },
    recordsFiltersPanel: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      p: { xs: 0, md: 0 },
      gap: 0,
    },
    recordsFiltersContent: {
      display: 'flex',
      flexDirection: 'column',
      // height: '100%',
      height: 'auto',
      minHeight: 0,
      width: '100%',
      boxSizing: 'border-box',
      p: { xs: 2, md: 2 },
      gap: 1.5,
      minWidth: { sm: 400 },
    },
    recordsFiltersHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      flexWrap: 'wrap',
    },
    recordsFiltersQuickFilters: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
    },
    recordsFiltersLeadFilters: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.5,
    },
    recordsFiltersTitle: {
      fontSize: typography.fontSizeLg,
      fontWeight: typography.fontWeightSemibold,
      color: text.primary,
    },
    recordsFiltersFields: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      flex: '1 1 0%',
      minHeight: 0,
      overflowY: 'auto',
      maxHeight: {xs:'250px' , sm: 'none'},
      pt: 1,
    },
    recordsFiltersFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      pt: 1,
      borderTop: `1px solid ${borders.border}`,
      flexShrink: 0,
      bgcolor: surfaces.surface,
    },
    recordsColumnsDrawerMobile: {
      width: '100%',
      maxHeight: '70vh',
      borderRadius: 0,
    },
    recordsColumnsDrawerDesktop: {
      width: 360,
      borderRadius: 0,
    },
    recordsColumnsPanel: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: { xs: 280, sm: '100%' },
      p: { xs: 2, md: 2.5 },
      gap: 0,
    },
    recordsColumnsHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1.5,
      pb: 1.5,
    },
    recordsColumnsHeaderText: {
      flex: 1,
      minWidth: 0,
    },
    recordsColumnsHelper: {
      mt: 0.75,
      fontSize: typography.fontSizeSm,
      lineHeight: 1.45,
      color: text.secondary,
      maxWidth: 280,
    },
    recordsColumnsCountChip: {
      borderRadius: radiusPx.xs,
      height: 24,
      fontSize: typography.fontSizeXs,
      fontWeight: typography.fontWeightMedium,
      bgcolor: surfaces.surfaceMuted,
      color: text.secondary,
      flexShrink: 0,
    },
    recordsColumnsDivider: {
      borderColor: borders.border,
    },
    recordsColumnsMasterRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      py: 0.5,
      borderBottom: `1px solid ${borders.border}`,
    },
    recordsColumnsItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      minHeight: 40,
      m: 0,
      px: 1,
      py: 0.5,
      borderRadius: radiusPx.sm,
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color 120ms ease',
      '&:hover': {
        bgcolor: surfaces.surfaceMuted,
      },
    },
    recordsColumnsItemBold: {
      flex: 1,
      minWidth: 0,
      px: 0,
      py: 0,
      minHeight: 36,
      '&:hover': {
        bgcolor: 'transparent',
      },
    },
    recordsColumnsCheckbox: {
      p: 0.5,
      flexShrink: 0,
      alignSelf: 'center',
    },
    recordsColumnsItemLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightRegular,
      lineHeight: typography.lineHeightTight,
      color: text.primary,
      display: 'block',
      pt: '1px',
    },
    recordsColumnsItemLabelBold: {
      fontWeight: typography.fontWeightMedium,
    },
    recordsColumnsResetButton: {
      flexShrink: 0,
      alignSelf: 'center',
      border:`1px solid ${borders.border}`,
      fontSize: typography.fontSizeXs,
      height:32
    },
    recordsColumnsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.125,
      overflowY: 'auto',
      flex: 1,
      py: 0.75,
      pr: 0.25,
    },
    recordsColumnsEmpty: {
      fontSize: typography.fontSizeSm,
      color: text.secondary,
      py: 2,
    },
    recordsColumnsFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      pt: 1.5,
      mt: 'auto',
      borderTop: `1px solid ${borders.border}`,
      position: { xs: 'sticky', md: 'static' },
      bottom: 0,
      bgcolor: surfaces.surface,
      '& Button':{height:32}
    },

    importRecordsCard: {
      border: `1px solid ${borders.border}`,
      borderRadius: radiusPx.md,
      bgcolor: surfaces.surface,
      p: { xs: 2, md: 3 },
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    importRecordsUploadZone: {
      border: `1px dashed ${borders.border}`,
      borderRadius: radiusPx.sm,
      p: { xs: 2.5, md: 3 },
      minHeight: 160,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      bgcolor: surfaces.surfaceSubtle,
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        borderColor: interactive.hoverBorder ?? accent.primaryBorder,
        bgcolor: surfaces.surface,
      },
      '&:focus-visible': {
        outline: `2px solid ${accent.primary ?? colors.blue[600]}`,
        outlineOffset: 2,
      },
    },
    importRecordsActionsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1.5,
    },
    importRecordsHeaderRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
    },
  };
}
